import retry from 'async-retry';
import { useCallback } from 'react';
import slugify from 'slugify';

import { toaster } from '@components/ui/toaster';
import { logError } from 'helpers/logging';
import { uuidv4 } from 'helpers/uuid';
import { IUserOrg } from 'stores/orgStore';
import { useStores } from 'stores/rootStore';

const MAX_RETRIES = 3;
export const useCreateOrg = ({
  onCancel,
  onOrgCreated,
}: {
  onCancel?: () => void;
  onOrgCreated?: (org: IUserOrg) => void;
}) => {
  const { orgStore, userStore } = useStores();

  const createOrg = useCallback(
    (name: string) => {
      const ownerId = userStore.user?.id;
      if (ownerId == null) {
        toaster.create({
          title: 'Something went wrong',
          description: 'Failed to create business. Missing owner id',
          type: 'error',
        });
        return;
      }

      retry(
        async () => {
          const uuid = uuidv4().slice(0, 8);
          const slug = slugify(`${name}-${uuid}`, {
            lower: true,
            strict: true,
          });
          const org = await orgStore.createOrg({ name, slug, ownerId });
          if (org == null) {
            throw new Error('Null response from API');
          }
          return org;
        },
        {
          retries: MAX_RETRIES,
        },
      )
        .then((org) => {
          onCancel?.();
          onOrgCreated?.(org);
        })
        .catch((error: Error) => {
          logError('Failed to create business', {
            name,
            ownerId,
            error,
          });
          toaster.create({
            title: 'Something went wrong',
            description: 'Failed to create business',
            type: 'error',
          });
        });
    },
    [onCancel, orgStore, userStore, onOrgCreated],
  );

  return { createOrg };
};
